import { InspectionCategory, InspectionTableConfig } from '../../../../types/adminDataInspection';

/**
 * Table configurations for all admin data inspection tables.
 * Grouped into 3 categories matching the backend controller endpoints.
 *
 * StatusConfig references:
 *   ST1  = Order/PList general status
 *   ST2  = Lock status
 *   ST25 = Internal transfer TransType
 *   ST26 = Internal transfer LineStatus
 *   ST27 = StcTransaction TransType
 *   ST32 = Item type
 *   ST24 = Pick rule
 */

// Shared table configs (shown in multiple categories)
const stcTransOrderTable: InspectionTableConfig = {
  tableName: 'stcTransOrders',
  endpoint: 'stc-trans-orders',
  label: 'StcTransOrder',
  description: 'Internordrer (mottak/flytt)',
  defaultSort: 'OrderDate',
  columns: [
    { key: 'batchID', label: 'BatchID', width: 120, sortable: true, filterType: 'text', linkTo: { targetTable: 'stcTrans', filterKey: 'batchID' } },
    { key: 'orderStatus', label: 'Status', width: 130, sortable: true, statusConfig: { statusType: 1 } },
    { key: 'orderType', label: 'Type', width: 140, sortable: true, filterType: 'text', statusConfig: { statusType: 45 } },
    { key: 'warehouse', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
    { key: 'operatorID', label: 'Operatør', width: 100, sortable: true, filterType: 'text' },
    { key: 'description1', label: 'Beskrivelse', width: 200, sortable: true, filterType: 'text' },
    { key: 'orderDate', label: 'Ordredato', width: 110, sortable: true, type: 'date', filterType: 'date' },
    { key: 'startWorkDateTime', label: 'Start', width: 140, sortable: true, type: 'date', filterType: 'date' },
    { key: 'stopWorkDateTime', label: 'Stopp', width: 140, sortable: true, type: 'date', filterType: 'date' },
    { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
    { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
  ]
};

const stcTransTable: InspectionTableConfig = {
  tableName: 'stcTrans',
  endpoint: 'stc-trans',
  label: 'StcTrans',
  description: 'Interne lagertransaksjoner',
  defaultSort: 'Created',
  columns: [
    { key: 'lineID', label: 'LinjeID', width: 80, sortable: true, type: 'number' },
    { key: 'lineType', label: 'LinjeType', width: 140, sortable: true, filterType: 'text', statusConfig: { statusType: 3 } },
    { key: 'lineStatus', label: 'LinjeStatus', width: 130, sortable: true, statusConfig: { statusType: 1 } },
    { key: 'transType', label: 'TransType', width: 80, sortable: true, type: 'number' },
    { key: 'itemNo', label: 'VareNr', width: 130, sortable: true, filterType: 'text', linkTo: { targetTable: 'items', filterKey: 'itemNo' } },
    { key: 'description1', label: 'Beskrivelse', width: 180, sortable: true, filterType: 'text' },
    { key: 'locationName', label: 'Fra lokasjon', width: 110, sortable: true, filterType: 'text' },
    { key: 'toLocationName', label: 'Til lokasjon', width: 110, sortable: true, filterType: 'text' },
    { key: 'quantity', label: 'Antall', width: 80, sortable: true, type: 'number' },
    { key: 'batchID', label: 'BatchID', width: 120, sortable: true, filterType: 'text', linkTo: { targetTable: 'stcTransOrders', filterKey: 'batchID' } },
    { key: 'warehouse', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
    { key: 'orderNo', label: 'OrdreNr', width: 90, sortable: true, type: 'number', linkTo: { targetTable: 'porders', filterKey: 'orderNo' } },
    { key: 'orderNoERP', label: 'ERP OrdreNr', width: 130, sortable: true, filterType: 'text' },
    { key: 'lineNoERP', label: 'ERP LinjeNr', width: 90, sortable: true, type: 'number' },
    { key: 'itemSerialNo', label: 'SerieNr', width: 110, sortable: true, filterType: 'text' },
    { key: 'itemBatchNo', label: 'BatchNr', width: 110, sortable: true, filterType: 'text' },
    { key: 'itemBatchExpDate', label: 'Utløpsdato', width: 110, sortable: true, type: 'date', filterType: 'date' },
    { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
    { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
    { key: 'transDt', label: 'TransDato', width: 140, sortable: true, type: 'date', filterType: 'date' },
    { key: 'operatorID', label: 'Operatør', width: 100, sortable: true, filterType: 'text' },
  ]
};

export const inspectionCategories: InspectionCategory[] = [
  // ============================================================
  // Category 1: Ordreflyt
  // ============================================================
  {
    id: 'ordreflyt',
    label: 'Ordreflyt',
    tables: [
      {
        tableName: 'orders',
        endpoint: 'orders',
        label: 'Orders',
        description: 'Salgsordrer',
        defaultSort: 'OrderNo',
        columns: [
          { key: 'orderNo', label: 'OrdreNr', width: 90, sortable: true, filterType: 'text', type: 'number' },
          { key: 'orderNoERP', label: 'ERP Nr', width: 120, sortable: true, filterType: 'text' },
          { key: 'orderStatus', label: 'Status', width: 130, sortable: true, statusConfig: { statusType: 1 } },
          { key: 'orderType', label: 'Type', width: 80, sortable: true, filterType: 'text' },
          { key: 'orderDate', label: 'Ordredato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'deliveryDate', label: 'Leveringsdato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'customerNo', label: 'KundeNr', width: 100, sortable: true, filterType: 'text' },
          { key: 'customerName', label: 'Kundenavn', width: 180, sortable: true, filterType: 'text' },
          { key: 'warehouse', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      {
        tableName: 'orderLines',
        endpoint: 'order-lines',
        label: 'OrderLine',
        description: 'Ordrelinjer',
        defaultSort: 'OrderNo',
        columns: [
          { key: 'orderNo', label: 'OrdreNr', width: 90, sortable: true, filterType: 'text', type: 'number', linkTo: { targetTable: 'orders', filterKey: 'orderNo' } },
          { key: 'orderNoERP', label: 'ERP OrdreNr', width: 130, sortable: true, filterType: 'text' },
          { key: 'lineNo', label: 'LinjeNr', width: 70, sortable: true, type: 'number' },
          { key: 'lineNoERP', label: 'ERP LinjeNr', width: 90, sortable: true, type: 'number' },
          { key: 'lineStatus', label: 'Status', width: 130, sortable: true, statusConfig: { statusType: 1 } },
          { key: 'itemNo', label: 'VareNr', width: 130, sortable: true, filterType: 'text', linkTo: { targetTable: 'items', filterKey: 'itemNo' } },
          { key: 'description1', label: 'Beskrivelse', width: 200, sortable: true, filterType: 'text' },
          { key: 'quantity', label: 'Antall', width: 80, sortable: true, type: 'number' },
          { key: 'quantityPicked', label: 'Plukket', width: 80, sortable: true, type: 'number' },
          { key: 'stdLocationName', label: 'Lokasjon', width: 110, sortable: true, filterType: 'text' },
          { key: 'warehouse', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'pListID', label: 'PListID', width: 80, sortable: true, type: 'number', linkTo: { targetTable: 'plists', filterKey: 'pListID' } },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      {
        tableName: 'plists',
        endpoint: 'plists',
        label: 'PList',
        description: 'Plukklister',
        defaultSort: 'PListID',
        columns: [
          { key: 'pListID', label: 'PListID', width: 80, sortable: true, filterType: 'text', type: 'number' },
          { key: 'pListStatus', label: 'Status', width: 130, sortable: true, statusConfig: { statusType: 1 } },
          { key: 'lockStatus', label: 'Låst', width: 100, sortable: true, statusConfig: { statusType: 2 } },
          { key: 'description1', label: 'Beskrivelse', width: 180, sortable: true, filterType: 'text' },
          { key: 'pListDate', label: 'Dato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'operatorID', label: 'Operatør', width: 100, sortable: true, filterType: 'text' },
          { key: 'numOfOrderLines', label: 'Linjer', width: 70, sortable: true, type: 'number' },
          { key: 'numOfOrderLinesPicked', label: 'Plukket', width: 70, sortable: true, type: 'number' },
          { key: 'priority', label: 'Prioritet', width: 70, sortable: true, type: 'number' },
          { key: 'filterGroupID', label: 'Filtergruppe', width: 100, sortable: true, filterType: 'text' },
          { key: 'startWorkDateTime', label: 'Start', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'stopWorkDateTime', label: 'Stopp', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      {
        tableName: 'plistLines',
        endpoint: 'plist-lines',
        label: 'PListLine',
        description: 'Plukklistelinjer',
        defaultSort: 'PListID',
        columns: [
          { key: 'pListID', label: 'PListID', width: 80, sortable: true, filterType: 'text', type: 'number', linkTo: { targetTable: 'plists', filterKey: 'pListID' } },
          { key: 'pListLineNo', label: 'LinjeNr', width: 70, sortable: true, type: 'number' },
          { key: 'orderNo', label: 'OrdreNr', width: 90, sortable: true, filterType: 'text', type: 'number', linkTo: { targetTable: 'orders', filterKey: 'orderNo' } },
          { key: 'lineNo', label: 'OrdreLinje', width: 80, sortable: true, type: 'number' },
          { key: 'lineStatus', label: 'Status', width: 130, sortable: true, statusConfig: { statusType: 1 } },
          { key: 'itemNo', label: 'VareNr', width: 130, sortable: true, filterType: 'text', linkTo: { targetTable: 'items', filterKey: 'itemNo' } },
          { key: 'qty', label: 'Antall', width: 80, sortable: true, type: 'number' },
          { key: 'qtyPicked', label: 'Plukket', width: 80, sortable: true, type: 'number' },
          { key: 'binCode', label: 'Lokasjon', width: 110, sortable: true, filterType: 'text' },
          { key: 'warehouseID', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'serialNo', label: 'SerieNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'batchNo', label: 'BatchNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'expiryDate', label: 'Utløpsdato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      {
        tableName: 'plistOrders',
        endpoint: 'plist-orders',
        label: 'PListOrders',
        description: 'Plukkliste-ordrekoblinger',
        defaultSort: 'PListID',
        columns: [
          { key: 'pListID', label: 'PListID', width: 80, sortable: true, filterType: 'text', type: 'number', linkTo: { targetTable: 'plists', filterKey: 'pListID' } },
          { key: 'orderNo', label: 'OrdreNr', width: 90, sortable: true, filterType: 'text', type: 'number', linkTo: { targetTable: 'orders', filterKey: 'orderNo' } },
          { key: 'pListOrderStatus', label: 'Status', width: 100, sortable: true, statusConfig: { statusType: 1 } },
          { key: 'numOfPackages', label: 'Kolli', width: 80, sortable: true, type: 'number' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      {
        tableName: 'stcReservations',
        endpoint: 'stc-reservations',
        label: 'StcReservation',
        description: 'Reservasjoner',
        defaultSort: 'ID',
        columns: [
          { key: 'itemNo', label: 'VareNr', width: 130, sortable: true, filterType: 'text', linkTo: { targetTable: 'items', filterKey: 'itemNo' } },
          { key: 'binCode', label: 'Lokasjon', width: 110, sortable: true, filterType: 'text' },
          { key: 'warehouseID', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'resQuantity', label: 'Antall', width: 80, sortable: true, type: 'number' },
          { key: 'resStatus', label: 'ResStatus', width: 110, sortable: true, type: 'number', statusConfig: { statusType: 39 } },
          { key: 'resDate', label: 'Dato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'orderNo', label: 'OrdreNr', width: 90, sortable: true, filterType: 'text', type: 'number', linkTo: { targetTable: 'orders', filterKey: 'orderNo' } },
          { key: 'lineNo', label: 'LinjeNr', width: 70, sortable: true, type: 'number' },
          { key: 'pListID', label: 'PListID', width: 80, sortable: true, type: 'number', linkTo: { targetTable: 'plists', filterKey: 'pListID' } },
          { key: 'batchNo', label: 'BatchNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'expiryDate', label: 'Utløpsdato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
    ]
  },

  // ============================================================
  // Category 2: Innkjøp & Mottak
  // ============================================================
  {
    id: 'innkjop',
    label: 'Innkjøp & Mottak',
    tables: [
      {
        tableName: 'porders',
        endpoint: 'porders',
        label: 'POrders',
        description: 'Innkjøpsordrer',
        defaultSort: 'OrderNo',
        columns: [
          { key: 'orderNo', label: 'OrdreNr', width: 90, sortable: true, filterType: 'text', type: 'number' },
          { key: 'orderNoERP', label: 'ERP Nr', width: 120, sortable: true, filterType: 'text' },
          { key: 'orderStatus', label: 'Status', width: 130, sortable: true, statusConfig: { statusType: 1 } },
          { key: 'supplierNo', label: 'LevNr', width: 100, sortable: true, filterType: 'text' },
          { key: 'supplierName', label: 'Leverandør', width: 180, sortable: true, filterType: 'text' },
          { key: 'orderDate', label: 'Ordredato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'deliveryDate', label: 'Leveringsdato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'warehouse', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      {
        tableName: 'porderLines',
        endpoint: 'porder-lines',
        label: 'POrderLine',
        description: 'Innkjøpsordrelinjer',
        defaultSort: 'OrderNo',
        columns: [
          { key: 'orderNo', label: 'OrdreNr', width: 90, sortable: true, filterType: 'text', type: 'number', linkTo: { targetTable: 'porders', filterKey: 'orderNo' } },
          { key: 'orderNoERP', label: 'ERP OrdreNr', width: 130, sortable: true, filterType: 'text' },
          { key: 'lineNo', label: 'LinjeNr', width: 70, sortable: true, type: 'number' },
          { key: 'lineNoERP', label: 'ERP LinjeNr', width: 90, sortable: true, type: 'number' },
          { key: 'lineStatus', label: 'Status', width: 130, sortable: true, statusConfig: { statusType: 1 } },
          { key: 'itemNo', label: 'VareNr', width: 130, sortable: true, filterType: 'text', linkTo: { targetTable: 'items', filterKey: 'itemNo' } },
          { key: 'description1', label: 'Beskrivelse', width: 200, sortable: true, filterType: 'text' },
          { key: 'quantity', label: 'Antall', width: 80, sortable: true, type: 'number' },
          { key: 'quantityReserved', label: 'Reservert', width: 80, sortable: true, type: 'number' },
          { key: 'quantityAvailable', label: 'Tilgjengelig', width: 90, sortable: true, type: 'number' },
          { key: 'itemUnitCode', label: 'Enhet', width: 70, sortable: true },
          { key: 'warehouse', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'deliveryDate', label: 'Leveringsdato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      stcTransOrderTable,
      stcTransTable,
      {
        tableName: 'stcIntRelocations',
        endpoint: 'stc-int-relocations',
        label: 'StcIntRelocation',
        description: 'Interne flyttinger',
        defaultSort: 'ID',
        columns: [
          { key: 'itemNo', label: 'VareNr', width: 130, sortable: true, filterType: 'text', linkTo: { targetTable: 'items', filterKey: 'itemNo' } },
          { key: 'binCode', label: 'Lokasjon', width: 110, sortable: true, filterType: 'text' },
          { key: 'quantity', label: 'Antall', width: 80, sortable: true, type: 'number' },
          { key: 'transType', label: 'TransType', width: 120, sortable: true, statusConfig: { statusType: 25 } },
          { key: 'lineStatus', label: 'LinjeStatus', width: 120, sortable: true, statusConfig: { statusType: 41 } },
          { key: 'warehouseID', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'operatorID', label: 'Operatør', width: 100, sortable: true, filterType: 'text' },
          { key: 'orderNo', label: 'OrdreNr', width: 90, sortable: true, filterType: 'text', type: 'number' },
          { key: 'lineNo', label: 'LinjeNr', width: 70, sortable: true, type: 'number' },
          { key: 'transDt', label: 'TransDato', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'grShipmentNo', label: 'GRShipment', width: 90, sortable: true, filterType: 'text', type: 'number' },
          { key: 'serialNo', label: 'SerieNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'batchNo', label: 'BatchNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'expiryDate', label: 'Utløpsdato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      {
        tableName: 'stcIntRelOrders',
        endpoint: 'stc-int-rel-orders',
        label: 'StcIntRelOrder',
        description: 'Interne relokasjonsordrer',
        defaultSort: 'StcOrderNo',
        columns: [
          { key: 'stcOrderNo', label: 'OrdreNr', width: 90, sortable: true, filterType: 'text', type: 'number' },
          { key: 'orderStatus', label: 'Status', width: 130, sortable: true, statusConfig: { statusType: 1 } },
          { key: 'transType', label: 'TransType', width: 120, sortable: true, statusConfig: { statusType: 25 } },
          { key: 'lockStatus', label: 'Låst', width: 100, sortable: true, filterType: 'text', statusConfig: { statusType: 2 } },
          { key: 'warehouseID', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'operatorID', label: 'Operatør', width: 100, sortable: true, filterType: 'text' },
          { key: 'orderPriority', label: 'Prioritet', width: 70, sortable: true, type: 'number' },
          { key: 'description1', label: 'Beskrivelse', width: 200, sortable: true, filterType: 'text' },
          { key: 'transDt', label: 'TransDato', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      {
        tableName: 'stcIntRelOrderLines',
        endpoint: 'stc-int-rel-order-lines',
        label: 'StcIntRelOrderLine',
        description: 'Interne relokasjonslinjer',
        defaultSort: 'StcOrderNo',
        columns: [
          { key: 'stcOrderNo', label: 'OrdreNr', width: 90, sortable: true, filterType: 'text', type: 'number', linkTo: { targetTable: 'stcIntRelOrders', filterKey: 'stcOrderNo' } },
          { key: 'stcLineNo', label: 'LinjeNr', width: 70, sortable: true, type: 'number' },
          { key: 'itemNo', label: 'VareNr', width: 130, sortable: true, filterType: 'text', linkTo: { targetTable: 'items', filterKey: 'itemNo' } },
          { key: 'description1', label: 'Beskrivelse', width: 180, sortable: true, filterType: 'text' },
          { key: 'quantity', label: 'Antall', width: 80, sortable: true, type: 'number' },
          { key: 'quantityMoved', label: 'Flyttet', width: 80, sortable: true, type: 'number' },
          { key: 'fromBinCode', label: 'Fra lokasjon', width: 110, sortable: true, filterType: 'text' },
          { key: 'toBinCode', label: 'Til lokasjon', width: 110, sortable: true, filterType: 'text' },
          { key: 'lineStatus', label: 'LinjeStatus', width: 130, sortable: true, statusConfig: { statusType: 40 } },
          { key: 'transType', label: 'TransType', width: 120, sortable: true, filterType: 'text', statusConfig: { statusType: 25 } },
          { key: 'warehouseID', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'movedBy', label: 'Flyttet av', width: 100, sortable: true, filterType: 'text' },
          { key: 'grShipmentNo', label: 'GRShipment', width: 90, sortable: true, type: 'number' },
          { key: 'serialNo', label: 'SerieNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'batchNo', label: 'BatchNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'expiryDate', label: 'Utløpsdato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
    ]
  },

  // ============================================================
  // Category 3: Lager & Beholdning
  // ============================================================
  {
    id: 'lager',
    label: 'Lager & Beholdning',
    tables: [
      {
        tableName: 'itemBins',
        endpoint: 'item-bins',
        label: 'ItemBins',
        description: 'Lagerbeholdning per lokasjon',
        defaultSort: 'ItemNo',
        columns: [
          { key: 'itemNo', label: 'VareNr', width: 130, sortable: true, filterType: 'text', linkTo: { targetTable: 'items', filterKey: 'itemNo' } },
          { key: 'binCode', label: 'Lokasjon', width: 110, sortable: true, filterType: 'text' },
          { key: 'warehouseID', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'quantity', label: 'Antall', width: 80, sortable: true, type: 'number' },
          { key: 'serialNo', label: 'SerieNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'batchNo', label: 'BatchNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'expiryDate', label: 'Utløpsdato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'grShipmentNo', label: 'GRShipment', width: 90, sortable: true, filterType: 'text', type: 'number' },
          { key: 'lastCountedDate', label: 'Siste telling', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastMovementDate', label: 'Siste bevegelse', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      {
        tableName: 'stcTransactions',
        endpoint: 'stc-transactions',
        label: 'StcTransaction',
        description: 'Plukk- og mottakstransaksjoner',
        defaultSort: 'ID',
        columns: [
          { key: 'transType', label: 'TransType', width: 130, sortable: true, statusConfig: { statusType: 27 } },
          { key: 'transDate', label: 'TransDato', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'itemNo', label: 'VareNr', width: 130, sortable: true, filterType: 'text', linkTo: { targetTable: 'items', filterKey: 'itemNo' } },
          { key: 'binCode', label: 'Lokasjon', width: 110, sortable: true, filterType: 'text' },
          { key: 'quantity', label: 'Antall', width: 80, sortable: true, type: 'number' },
          { key: 'warehouseID', label: 'Lager', width: 70, sortable: true, filterType: 'text' },
          { key: 'operatorID', label: 'Operatør', width: 100, sortable: true, filterType: 'text' },
          { key: 'orderNo', label: 'OrdreNr', width: 90, sortable: true, type: 'number', linkTo: { targetTable: 'orders', filterKey: 'orderNo' } },
          { key: 'lineNo', label: 'LinjeNr', width: 70, sortable: true, type: 'number' },
          { key: 'pListID', label: 'PListID', width: 80, sortable: true, type: 'number', linkTo: { targetTable: 'plists', filterKey: 'pListID' } },
          { key: 'pListLineNo', label: 'PListLinje', width: 80, sortable: true, type: 'number' },
          { key: 'grShipmentNo', label: 'GRShipment', width: 90, sortable: true, filterType: 'text', type: 'number' },
          { key: 'serialNo', label: 'SerieNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'batchNo', label: 'BatchNr', width: 110, sortable: true, filterType: 'text' },
          { key: 'expiryDate', label: 'Utløpsdato', width: 110, sortable: true, type: 'date', filterType: 'date' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
      stcTransOrderTable,
      stcTransTable,
      {
        tableName: 'items',
        endpoint: 'items',
        label: 'Items',
        description: 'Varer',
        defaultSort: 'ItemNo',
        columns: [
          { key: 'itemNo', label: 'VareNr', width: 130, sortable: true, filterType: 'text' },
          { key: 'description1', label: 'Beskrivelse', width: 220, sortable: true, filterType: 'text' },
          { key: 'itemType', label: 'Varetype', width: 120, sortable: true, statusConfig: { statusType: 32 } },
          { key: 'itemBarCode', label: 'Strekkode', width: 130, sortable: true, filterType: 'text' },
          { key: 'stdLocationName', label: 'Std.lokasjon', width: 110, sortable: true, filterType: 'text' },
          { key: 'quantity', label: 'Beholdning', width: 90, sortable: true, type: 'number' },
          { key: 'quantityReserved', label: 'Reservert', width: 80, sortable: true, type: 'number' },
          { key: 'quantityAvailable', label: 'Tilgjengelig', width: 90, sortable: true, type: 'number' },
          { key: 'pickRuleNo', label: 'Plukkregel', width: 120, sortable: true, statusConfig: { statusType: 24 } },
          { key: 'itemUnitCode', label: 'Enhet', width: 70, sortable: true },
          { key: 'purchasePrice', label: 'Innkjøpspris', width: 100, sortable: true, type: 'number' },
          { key: 'created', label: 'Opprettet', width: 140, sortable: true, type: 'date', filterType: 'date' },
          { key: 'lastUpdate', label: 'Oppdatert', width: 140, sortable: true, type: 'date', filterType: 'date' },
        ]
      },
    ]
  },
];
